import React, { useEffect, useState, useRef } from 'react';
import { isEmbeddedPanel, parseUrl, screenNameFor, urlForView } from './utils/navigation';
import { useDefaultLanding } from './hooks/useDefaultLanding';
import { canOpenApprovalsDesk, extractRoleNames, type RoleBearingUser } from './utils/permissions';
import { useMyProfile } from './hooks/useMyProfile';
import { ActingAsProvider } from './contexts/ActingAsContext';
import { UserProvider, type UserData } from './contexts/UserContext';
export type { UserData } from './contexts/UserContext';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Capacitor } from '@capacitor/core';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { useOfflineSync } from './hooks/useOfflineSync';
import { useRegisterSW } from 'virtual:pwa-register/react';
import Login from './components/auth/Login';
import RequestAccess from './components/auth/RequestAccess';
import SetInvitePassword from './components/auth/SetInvitePassword';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import Support from './components/legal/Support';
import DocketSignPage from './components/mechanical/movements/DocketSignPage';
import ValveClientApprovalPage from './components/mechanical/valves/ValveClientApprovalPage';
import VendorPrequalApply from './components/public/VendorPrequalApply';
import ProcurementClientRequestApply from './components/public/ProcurementClientRequestApply';
import VendorRequalify from './components/public/VendorRequalify';
import RosterConfirm from './components/public/RosterConfirm';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import ProcurementEstimateDetail from './components/jobs/estimates/ProcurementEstimateDetail';
import ClientInvoiceDetail from './components/financial/invoices/ClientInvoiceDetail';
import PayslipsSelfService from './components/HR/PayslipsSelfService';
import OfflineBanner from './components/shared/OfflineBanner';
import LoadingSpinner from './components/shared/LoadingSpinner';
import { useNotification } from './components/shared/NotificationModal';
import { ChatbotWidget } from './components/chatbot';
import { ModalProvider } from './contexts/ModalContext';
import {
  SidebarProvider,
  SIDEBAR_COLLAPSED_KEY,
  mainOffsetClass,
} from './contexts/SidebarContext';
import TopBar from './components/TopBar';
import { hasUnsavedWork, onUnsavedWorkChange } from './utils/unsavedWork';
import { answerTabPings, otherTabsOpen } from './utils/appTabs';
import {
  setAuthUser,
  getAuthUser,
  getAuthUserSync,
  removeAuthUser,
  removeAuthUserSync,
  initializeAuthStorage,
  setPendingDeepLink,
  consumePendingDeepLink,
  removePendingDeepLink,
} from './utils/authStorage';
import { isAccountInactiveError } from './utils/auth';
import { initPushNotifications, unregisterPushToken } from './services/pushNotificationService';
import './App.css';
import CloseButton from './components/shared/CloseButton';

const API_BASE = import.meta.env.VITE_API_BASE || '';

// Loaded only inside a SharePoint frame; it carries the desk and its queues.
const ApprovalsDeskPanel = React.lazy(() => import('./components/home/ApprovalsDeskPanel'));

// QR redirect: nginx SPA catch-all serves index.html for /qr/... paths.
// Redirect to /api/qr/... which nginx proxies to the backend for QR lookup & redirect.
if (window.location.pathname.startsWith('/qr/')) {
  const qrPath = window.location.pathname.replace(/^\/qr\//, '/api/qr/');
  window.location.replace(`${API_BASE || ''}${qrPath}`);
}

interface JwtPayload {
  userId?: string;
  email?: string;
  role?: string;
  roles?: string[];
  isActive?: boolean;
  fullName?: string;
  exp?: number;
}

// UserData is imported and re-exported from ./contexts/UserContext

interface MeResponse {
  ok?: boolean;
  id?: string;
  name?: string;
  fullName?: string;
  email?: string;
  personCode?: string;
  roles?: string[];
  _computedRoles?: string[];
  role?: string;
  isActive?: boolean;
  userId?: string;
  _id?: string;
}

interface NavigateOptions {
  requestId?: string | null;
  invoiceId?: string | null;
  jobId?: string | null;
  rateId?: string | null;
  clientCode?: string | null;
}

type Environment = 'LOCAL' | 'STAGING' | 'PRODUCTION';

function decodeJwt(token: string): JwtPayload {
  try {
    const [, payload] = token.split('.');
    return JSON.parse(atob(payload));
  } catch {
    return {};
  }
}

function isJwtExpired(token: string): boolean {
  const { exp } = decodeJwt(token) || {};
  if (!exp) return false;
  const now = Math.floor(Date.now() / 1000) + 30;
  return exp < now;
}

// Check if token needs refresh (less than 7 days remaining)
const TOKEN_REFRESH_THRESHOLD_SECONDS = 7 * 24 * 60 * 60; // 7 days in seconds

function shouldRefreshToken(token: string): boolean {
  const { exp } = decodeJwt(token) || {};
  if (!exp) return false;
  const now = Math.floor(Date.now() / 1000);
  const timeRemaining = exp - now;
  return timeRemaining > 0 && timeRemaining < TOKEN_REFRESH_THRESHOLD_SECONDS;
}

export default function App() {
  const notification = useNotification();
  const [user, setUser] = useState<UserData | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<string>('Home');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  // Desktop collapse — remembered per browser so an admin who works in the
  // roster planner all day doesn't re-hide the nav on every load.
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isSidebarCollapsed));
    } catch {
      /* ignore */
    }
  }, [isSidebarCollapsed]);
  const [selectedRateHeaderId, setSelectedRateHeaderId] = useState<string | null>(null);
  const [selectedClientCode, setSelectedClientCode] = useState<string | null>(null);
  const [urlEstimateId, setUrlEstimateId] = useState<string | null>(null);
  const [urlInvoiceId, setUrlInvoiceId] = useState<string | null>(null);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [showAccountPending, setShowAccountPending] = useState<boolean>(false);
  const [isBannerDismissed, setIsBannerDismissed] = useState<boolean>(false);

  // Mobile platform detection
  const [isNativePlatform, setIsNativePlatform] = useState<boolean>(false);
  const isOnline = useOnlineStatus();
  const { syncProgress, pendingCount: _pendingCount } = useOfflineSync(isOnline);

  // Page-level drag/drop safety net. Without this, dropping a file (e.g. a
  // PDF) anywhere on the page that isn't covered by an attachment dropzone
  // makes the browser navigate to render the file directly — losing the
  // user's current view. Each dropzone (AttachmentUpload, BulkUploadSection,
  // FileDropZone, etc.) calls preventDefault on its own React onDragOver /
  // onDrop handlers; this window-level listener catches everything that
  // didn't land on a dropzone and stops the browser from accepting it.
  // React's synthetic onDrop still fires first inside dropzones, so this
  // doesn't interfere with legitimate uploads.
  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      // Only intercept file drags — leave HTML5 element dragging (sortable
      // lists, etc.) alone by checking dataTransfer.types for "Files".
      if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  // Show sync toast when auto-sync completes
  useEffect(() => {
    if (!syncProgress.isSyncing && syncProgress.total > 0 && syncProgress.synced > 0) {
      const msg = `Synced ${syncProgress.synced} offline inspection(s)${
        syncProgress.failed > 0 ? `, ${syncProgress.failed} failed` : ''
      }${syncProgress.warned > 0 ? `, ${syncProgress.warned} with warnings` : ''}`;
      if (syncProgress.failed > 0 || syncProgress.warned > 0) {
        notification.showWarning(msg);
      } else {
        notification.showSuccess(msg);
      }
    }
  }, [syncProgress.isSyncing, syncProgress.total, syncProgress.synced, syncProgress.failed, syncProgress.warned]); // eslint-disable-line react-hooks/exhaustive-deps
  // Network status is handled by networkService.ts and consumed by OfflineBanner

  // Service worker registration and update checks. There is deliberately no
  // "Update available" prompt (it covered part of the screen and was hidden on
  // request): a new version downloads in the background and then takes over
  // at a moment nobody is in the middle of something (below).
  const [openedAt, setOpenedAt] = useState(() => Date.now());
  const {
    needRefresh: [updateWaiting, setUpdateWaiting],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        // Check for SW updates every 60 minutes
        setInterval(() => registration.update(), 60 * 60 * 1000);
      }
    },
  });

  // A downloaded version used to wait until EVERY GRAutomates tab was closed,
  // so anyone who kept one open -- or just logged out and in -- stayed on the
  // old app for days after a deploy. Taking it reloads every open tab (the
  // service-worker library does that itself, bypassing the unsaved-work guard
  // below), so it is taken only when that cannot cost anyone work:
  //   - just after this tab opened, while it is the ONLY GRAutomates tab and
  //     nothing is unsaved -- the reload is invisible;
  //   - at the very end of logging out (handleLogout).
  // Otherwise it waits, as before, for every tab to close. Never while a
  // dialog or drawer is open: most forms live in one, and the unsaved-work
  // guard knows only the timesheet and course builder.
  //
  // The phone app is rarely "opened": it is resumed from the background, and
  // that never reloads the page, so a phone stayed on the version it loaded
  // days earlier (roster attachments, fixed 14 Oct, still failed on phones a
  // day later with the old build's error). There, coming back to the
  // foreground after RESUME_AS_OPEN_MS away counts as opening: it checks for a
  // new version and restarts the 30 seconds, under the same rules. Not a
  // shorter absence: the camera, scanner and file picker also background the
  // app, and a call or a quick look at Outlook leaves a half-filled form —
  // after half an hour the phone may well have closed the app itself.
  useEffect(answerTabPings, []);
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !('serviceWorker' in navigator)) return undefined;
    const RESUME_AS_OPEN_MS = 30 * 60_000;
    let hiddenAt: number | null = null;
    const onHide = () => {
      hiddenAt ??= Date.now();
    };
    const onShow = () => {
      const away = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      if (away < RESUME_AS_OPEN_MS) return;
      setOpenedAt(Date.now());
      void navigator.serviceWorker
        .getRegistration()
        .then(async r => {
          if (!r) return;
          await r.update();
          // The service-worker library notices only the first new version after
          // the page loads, so one whose download failed on site and finished on
          // a later resume would wait unseen. Read the registration itself.
          const markIfWaiting = () => {
            if (r.waiting && navigator.serviceWorker.controller) setUpdateWaiting(true);
          };
          markIfWaiting();
          r.installing?.addEventListener('statechange', markIfWaiting);
        })
        .catch(() => undefined); // offline on site: the next resume tries again
    };
    // Capacitor's own pause/resume, and the page's visibility as well: which
    // of the two a given phone's web view fires is not something to rely on.
    const onVisibility = () => (document.visibilityState === 'hidden' ? onHide() : onShow());
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('pause', onHide);
    document.addEventListener('resume', onShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('pause', onHide);
      document.removeEventListener('resume', onShow);
    };
  }, [setUpdateWaiting]);
  useEffect(() => {
    if (!updateWaiting || Date.now() - openedAt > 30_000) return undefined;
    let cancelled = false;
    void otherTabsOpen().then(others => {
      const dialogOpen = !!document.querySelector('[aria-modal="true"]');
      if (!cancelled && !others && !hasUnsavedWork() && !dialogOpen) void updateServiceWorker(true);
    });
    return () => {
      cancelled = true;
    };
  }, [updateWaiting, updateServiceWorker, openedAt]);

  // iOS PWA fix: when a new SW activates mid-session (skipWaiting + clientsClaim),
  // the open page still references old hashed asset filenames that no longer exist
  // in the new precache. iOS Safari does not auto-reload on controllerchange the
  // way Chrome does, leaving the app broken until reinstall. Force a clean reload
  // here so the page gets fresh HTML with the correct asset URLs.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    // If the page loaded WITHOUT a controller, the first controllerchange is the
    // initial clientsClaim (first visit / post-deploy / cache clear), NOT an SW
    // update — reloading there interrupts the user mid-interaction and looks like
    // "the page refreshes if I open something too quickly". Only reload when a new
    // SW replaces an existing controller (a genuine update).
    const hadControllerAtLoad = !!navigator.serviceWorker.controller;
    let reloading = false;
    let offUnsaved: (() => void) | null = null;
    const doReload = () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    };
    const handleControllerChange = () => {
      if (!hadControllerAtLoad || reloading) return;
      // Never yank the page out from under someone mid-edit. If there is
      // unsaved work (e.g. a timesheet being entered), defer the deploy reload
      // until that work is saved/closed, then reload for fresh assets. Data is
      // also autosaved locally, so this is purely to avoid interrupting typing.
      if (hasUnsavedWork()) {
        if (!offUnsaved) {
          offUnsaved = onUnsavedWorkChange(() => {
            if (!hasUnsavedWork()) {
              offUnsaved?.();
              offUnsaved = null;
              doReload();
            }
          });
        }
        return;
      }
      doReload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      offUnsaved?.();
    };
  }, []);

  // track if we are currently updating URL to avoid loops
  const isPushingRef = useRef<boolean>(false);

  const getEnvironment = (): Environment => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'LOCAL';
    if (hostname.includes('staging')) return 'STAGING';
    return 'PRODUCTION';
  };

  const environment = getEnvironment();

  // Axios baseURL
  if (process.env.NODE_ENV === 'development') {
    axios.defaults.baseURL = '';
  } else {
    axios.defaults.baseURL = API_BASE || '';
  }
  axios.defaults.withCredentials = false;

  const applyAxiosAuth = (token: string | null) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  async function hydrateUserFromMe(token: string): Promise<void> {
    try {
      if (!token) return;
      const r = await axios.get<MeResponse>('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const me = r.data || {};

      setUser((prev) => {
        // /auth/me returns: { ok, id, name, fullName, email, personCode, roles }
        // roles is already an array of strings from shapeUser() function
        const merged: UserData = {
          ...(prev || {}),
          token: token,
          userId: prev?.userId || me?.id || me?.userId || me?._id,
          email: prev?.email || me?.email,
          role: prev?.role || me?.role,
          isActive: prev?.isActive ?? me?.isActive ?? true,
          roles: Array.isArray(me?.roles) ? me.roles : prev?.roles || [],
          _computedRoles: Array.isArray(me?._computedRoles) ? me._computedRoles : Array.isArray(me?.roles) ? me.roles : prev?._computedRoles || [],
          fullName: prev?.fullName || me?.fullName || me?.name,
          personCode: me?.personCode || prev?.personCode,
        };

        // Use async storage (fire-and-forget since we can't await in setState callback)
        setAuthUser(merged).catch(() => {});
        return merged;
      });
    } catch {
      // unauthenticated is fine here
    }
  }

  // Interceptors
  useEffect(() => {
    // REQUEST: inject Authorization from localStorage if missing
    const reqId = axios.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        try {
          if (!config.headers) config.headers = {} as any;
          if (!config.headers.Authorization) {
            // Use sync version for interceptors (localStorage-based, also synced from Preferences on mobile)
            const parsed = getAuthUserSync<UserData>();
            if (parsed?.token) {
              config.headers.Authorization = `Bearer ${parsed.token}`;
            }
          }
        } catch {
          // Ignore errors
        }
        return config;
      },
      (err) => Promise.reject(err)
    );

    // RESPONSE: only log out when an authenticated API call fails for auth reasons
    const resId = axios.interceptors.response.use(
      (resp) => resp,
      async (err: AxiosError) => {
        const status = err?.response?.status;
        const message = (err?.response?.data as any)?.error || '';
        const hadAuthHeader =
          !!(err?.config?.headers as any)?.Authorization ||
          !!axios?.defaults?.headers?.common?.Authorization;

        let path = '';
        try {
          const u = new URL(err?.config?.url || '', window.location.origin);
          path = u.pathname || '';
        } catch {
          path = String(err?.config?.url || '');
        }
        const isApiOrAuth = /^\/(api|auth)\b/i.test(path);
        const isRefreshCall = /\/auth\/refresh\b/i.test(path);
        const messageLooksAuthy =
          /invalid|expired token|jwt malformed|signature|unauthori[sz]ed/i.test(message);
        const tokenBad =
          (status === 401 || status === 403) && (messageLooksAuthy || status === 401);
        const skipLogout = err?.config?.headers?.['X-Skip-Auth-Logout'] === 'true';

        // Before logging anyone out, try ONE silent token refresh + replay of
        // the failed request. A 401 means the server did not act on the request
        // (so replaying is safe), and this rescues a transient/edge-expired
        // token without bumping someone out of, e.g., a half-entered timesheet.
        // Guarded against loops via a per-request _authRetried flag, and scoped
        // to real API 401s (never the refresh call itself).
        const cfg = err?.config as (InternalAxiosRequestConfig & { _authRetried?: boolean }) | undefined;
        if (
          status === 401 && hadAuthHeader && isApiOrAuth && !skipLogout &&
          !isRefreshCall && cfg && !cfg._authRetried
        ) {
          cfg._authRetried = true;
          try {
            const current = getAuthUserSync<UserData>();
            if (current?.token) {
              const refreshResp = await axios.post('/auth/refresh', {}, {
                headers: { Authorization: `Bearer ${current.token}`, 'X-Skip-Auth-Logout': 'true' },
              });
              const newToken = refreshResp.data?.token;
              if (newToken) {
                const refreshedUser: UserData = {
                  ...current,
                  token: newToken,
                  roles: refreshResp.data.user?.roles || current.roles,
                  personCode: refreshResp.data.user?.personCode || current.personCode,
                };
                await setAuthUser(refreshedUser);
                setUser(refreshedUser);
                applyAxiosAuth(newToken);
                cfg.headers = cfg.headers || ({} as any);
                (cfg.headers as any).Authorization = `Bearer ${newToken}`;
                return axios(cfg); // replay the original request once
              }
            }
          } catch {
            // Refresh failed — fall through to the normal logout below.
          }
        }

        // A deactivated account: show Request Access, not a page of failing
        // calls. In memory only, so once reactivated it works on the next load.
        if (isAccountInactiveError(err)) {
          setUser(prev => (prev && prev.isActive !== false ? { ...prev, isActive: false } : prev));
        }

        if (tokenBad && hadAuthHeader && isApiOrAuth && !skipLogout) {
          // Use sync version for interceptors, also trigger async cleanup for mobile
          removeAuthUserSync();
          removeAuthUser().catch(() => {});
          applyAxiosAuth(null);
          setUser(null);
        }
        return Promise.reject(err);
      }
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []);

  // Mobile platform initialization
  useEffect(() => {
    const isMobile = Capacitor.isNativePlatform();
    setIsNativePlatform(isMobile);

    if (isMobile) {
      console.info('[Mobile] Running on native platform:', Capacitor.getPlatform());

      // Initialize persistent auth storage (migrates localStorage to native Preferences)
      initializeAuthStorage().then(() => {
        console.info('[Mobile] Auth storage initialized');
      }).catch(err => {
        console.error('[Mobile] Failed to initialize auth storage:', err);
      });
    }
    // Network monitoring is handled by networkService.ts (works on both web and native)
  }, []);

  // Initialize push notifications when user is authenticated on native platform
  useEffect(() => {
    if (isNativePlatform && user?.token) {
      initPushNotifications().catch(err => {
        console.error('[Mobile] Push notification init failed:', err);
      });
    }
  }, [isNativePlatform, user?.token]);

  // Session bootstrap + initial URL parse
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const samlToken = params.get('samlToken');
    const oauthToken = params.get('oauthToken');
    const inviteTokenParam = params.get('inviteToken');
    const errorParam = params.get('error');

    // Check for invite token
    if (inviteTokenParam) {
      setInviteToken(inviteTokenParam);
      setAuthLoading(false);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // Check for account_pending error from SSO
    if (errorParam === 'account_pending') {
      setShowAccountPending(true);
      setAuthLoading(false);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    const finishLogin = async (token: string) => {
      try {
        const payload = decodeJwt(token);
        const userData: UserData = {
          token,
          userId: payload.userId,
          email: payload.email,
          role: payload.role,
          isActive: payload.isActive ?? true,
          fullName: payload.fullName,
          roles: Array.isArray(payload.roles) ? payload.roles : [],
          _computedRoles: Array.isArray(payload.roles) ? payload.roles : [],
        };
        // Use persistent auth storage (Capacitor Preferences on mobile, localStorage on web)
        await setAuthUser(userData );
        setUser(userData);
        applyAxiosAuth(token);
        await hydrateUserFromMe(token);

        // Check for saved deep link URL (e.g., from email link clicked before login)
        const savedDeepLink = await consumePendingDeepLink();
        if (savedDeepLink) {
          window.location.href = savedDeepLink;
        }
      } catch (error) {
        console.error('Login error:', error);
      }
    };

    if (samlToken || oauthToken) {
      const token = samlToken || oauthToken;
      if (!token) { setAuthLoading(false); return; }
      if (isJwtExpired(token)) {
        // Token expired - clear auth storage
        removeAuthUser().catch(() => {});
        removePendingDeepLink().catch(() => {});
        applyAxiosAuth(null);
        setAuthLoading(false);
      } else {
        finishLogin(token)
          .catch((err) => console.error('Login failed:', err))
          .finally(() => setAuthLoading(false));
      }
      // Drop the token from the address but keep the rest: the embedded panel
      // (?view=ApprovalsDesk&embed=1) arrives with its token on that address.
      params.delete('samlToken');
      params.delete('oauthToken');
      const rest = params.toString();
      window.history.replaceState({}, '', rest ? `${window.location.pathname}?${rest}` : window.location.pathname);
    } else {
      // Load user from persistent storage (async for mobile reliability)
      const loadStoredUser = async () => {
        try {
          const parsed = await getAuthUser<UserData>();
          if (parsed) {
            if (!parsed?.token || isJwtExpired(parsed.token)) {
              await removeAuthUser();
              applyAxiosAuth(null);
              setUser(null);
            } else {
              setUser(parsed);
              applyAxiosAuth(parsed.token);
              hydrateUserFromMe(parsed.token);
            }
          }
        } catch {
          await removeAuthUser().catch(() => {});
        } finally {
          setAuthLoading(false);
        }
      };
      loadStoredUser();
    }

    // Parse current URL to set initial view/deep link
    const parsed = parseUrl();
    setUrlEstimateId(parsed.urlEstimateId);
    setUrlInvoiceId(parsed.urlInvoiceId);
    if (!parsed.urlEstimateId && !parsed.urlInvoiceId) {
      setActiveItem(parsed.view || 'Home');
      setSelectedRequestId(parsed.ids.requestId || null);
      setSelectedInvoiceId(parsed.ids.invoiceId || null);
      setSelectedJobId(parsed.ids.jobId || null);
      setSelectedRateHeaderId(parsed.ids.rateId || null);
      setSelectedClientCode(parsed.ids.clientCode || null);
    }

  }, []);

  // Auto-login for local development (only works on localhost)
  useEffect(() => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const hasSamlToken = new URLSearchParams(window.location.search).get('samlToken');

    // Auto-login ONLY for localhost development (NOT staging - staging uses Azure AD SSO)
    if (isDevelopment && isLocalhost && !user && !hasSamlToken) {
      // Wait a moment to let any existing tokens be processed
      const timer = setTimeout(async () => {
        const stored = await getAuthUser();
        if (!stored) {
          window.location.href = `${API_BASE}/auth/dev-login?email=admin@localhost.dev`;
        }
      }, 500);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [user]);

  // Automatic token refresh - keeps users logged in long-term
  useEffect(() => {
    if (!user?.token) return;

    const refreshToken = async () => {
      try {
        if (!user?.token || isJwtExpired(user.token)) return;
        if (!shouldRefreshToken(user.token)) return;

        const response = await axios.post('/auth/refresh', {}, {
          headers: { Authorization: `Bearer ${user.token}` },
        });

        if (response.data?.token) {
          const newUserData: UserData = {
            ...user,
            token: response.data.token,
            roles: response.data.user?.roles || user.roles,
            personCode: response.data.user?.personCode || user.personCode,
          };
          // Use persistent auth storage
          await setAuthUser(newUserData );
          setUser(newUserData);
          applyAxiosAuth(response.data.token);
        }
      } catch {
        // Silently fail - user will be prompted to login when token actually expires
      }
    };

    // Check immediately on mount
    refreshToken();

    // Check every hour
    const interval = setInterval(refreshToken, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.token]);

  // Handle browser Back/Forward
  useEffect(() => {
    function handlePop() {
      if (isPushingRef.current) return; // ignore our own pushes
      const parsed = parseUrl();
      setUrlEstimateId(parsed.urlEstimateId);
      setUrlInvoiceId(parsed.urlInvoiceId);
      if (!parsed.urlEstimateId && !parsed.urlInvoiceId) {
        setActiveItem(parsed.view || 'Home');
        setSelectedRequestId(parsed.ids.requestId || null);
        setSelectedInvoiceId(parsed.ids.invoiceId || null);
        setSelectedJobId(parsed.ids.jobId || null);
        setSelectedRateHeaderId(parsed.ids.rateId || null);
        setSelectedClientCode(parsed.ids.clientCode || null);
      }
    }
    window.addEventListener('popstate', handlePop);
    window.addEventListener('hashchange', handlePop);
    return () => {
      window.removeEventListener('popstate', handlePop);
      window.removeEventListener('hashchange', handlePop);
    };
  }, []);

  // Default landing for a URL that names no view: approvals:desk holders (and
  // approvers the server grants it to) to their queue,
  // everyone else to Home. MainContent is held back until it settles. The
  // rules (timeout, settle-once, explicit navigation wins) live in the hook.
  const myProfile = useMyProfile(user?.token);
  const { landingPending, endLandingHold } = useDefaultLanding(
    user?.token,
    setActiveItem,
    user?.userId || user?.email,
    canOpenApprovalsDesk(extractRoleNames(user as RoleBearingUser))
  );

  const handleLogin = async (userData: UserData) => {
    if (!userData?.token || isJwtExpired(userData.token)) {
      notification.showError('Your session token is expired. Please sign in again.', 'Session Expired');
      await removeAuthUser();
      applyAxiosAuth(null);
      setUser(null);
      return;
    }
    // Ensure _computedRoles is populated from roles if not already set
    if (!Array.isArray(userData._computedRoles) || userData._computedRoles.length === 0) {
      userData._computedRoles = Array.isArray(userData.roles) ? userData.roles : [];
    }
    // Use persistent auth storage
    await setAuthUser(userData );
    setUser(userData);
    applyAxiosAuth(userData.token);
    // Clear the invite-token gate now that the user is fully authenticated.
    // Without this, the App's `if (inviteToken)` check at the top of render
    // keeps SetInvitePassword mounted (with its "Setting Password..." spinner
    // visible) even after a successful password set — the user appeared
    // stuck until they hit Cancel because that's the only path that
    // previously cleared inviteToken. Cleared AFTER setUser + applyAxiosAuth
    // so the main app renders with axios already carrying the new JWT.
    setInviteToken(null);
    await hydrateUserFromMe(userData.token);
  };

  const handleLogout = async () => {
    // Unregister push token before clearing auth (best-effort)
    await unregisterPushToken();
    // Clear from persistent storage (both Capacitor Preferences and localStorage)
    await removeAuthUser();
    await removePendingDeepLink();
    setUser(null);
    applyAxiosAuth(null);
    // A waiting new version takes over only now, with sign-out complete: its
    // reload must never land before the session is cleared.
    if (updateWaiting) void updateServiceWorker(true);
    window.location.href = '/';
  };

  /** Central navigation: use this instead of setActiveItem directly */
  const navigate = (view: string, opts: NavigateOptions = {}) => {
    // set state first; an explicit choice ends any default-landing wait
    endLandingHold();
    setActiveItem(view);
    if (opts.requestId !== undefined) setSelectedRequestId(opts.requestId || null);
    if (opts.invoiceId !== undefined) setSelectedInvoiceId(opts.invoiceId || null);
    if (opts.jobId !== undefined) setSelectedJobId(opts.jobId || null);
    if (opts.rateId !== undefined) setSelectedRateHeaderId(opts.rateId || null);
    if (opts.clientCode !== undefined) setSelectedClientCode(opts.clientCode || null);

    // push URL
    const url = urlForView(view, {
      requestId: opts.requestId ?? selectedRequestId,
      invoiceId: opts.invoiceId ?? selectedInvoiceId,
      jobId: opts.jobId ?? selectedJobId,
      rateId: opts.rateId ?? selectedRateHeaderId,
      clientCode: opts.clientCode ?? selectedClientCode,
    });
    try {
      isPushingRef.current = true;
      window.history.pushState({}, '', url);
    } finally {
      // release in next tick to allow popstate, if any, to be ignored
      setTimeout(() => {
        isPushingRef.current = false;
      }, 0);
    }
  };

  // Helper used by deep-link detail screens to go "back to list" inside SPA
  const backOrList = (listPath: string, afterClear?: () => void) => {
    afterClear?.();
    try {
      isPushingRef.current = true;
      window.history.pushState({}, '', listPath);
    } finally {
      setTimeout(() => {
        isPushingRef.current = false;
      }, 0);
    }
  };

  // Show invite password setup if invite token is present
  if (inviteToken) {
    return (
      <div className="pt-safe-mobile">
        <SetInvitePassword
          inviteToken={inviteToken}
          onSuccess={handleLogin}
          onCancel={() => {
            setInviteToken(null);
            window.location.href = '/';
          }}
        />
      </div>
    );
  }

  // Show transport docket signing page (public, no auth required)
  if (window.location.pathname === '/docket-sign') {
    return <div className="pt-safe-mobile"><DocketSignPage /></div>;
  }

  // A client's approve / decline page for a valve inspection (public, from the emailed link)
  if (window.location.pathname === '/valve-approval') {
    return <div className="pt-safe-mobile"><ValveClientApprovalPage /></div>;
  }

  // Show privacy policy page (accessible without authentication)
  if (window.location.pathname === '/privacy-policy') {
    return <div className="pt-safe-mobile"><PrivacyPolicy /></div>;
  }

  // Show support page (accessible without authentication)
  if (window.location.pathname === '/support') {
    return <div className="pt-safe-mobile"><Support /></div>;
  }

  // Public Vendor Prequalification form (accessible without authentication)
  if (window.location.pathname.startsWith('/vendor-prequal/apply')) {
    return <VendorPrequalApply />;
  }

  // Public re-qualification form — token-gated, no login
  // Roster swing confirmation link from an email (token-gated, no sign-in).
  if (window.location.pathname === '/roster-confirm') {
    return <RosterConfirm />;
  }

  if (window.location.pathname.startsWith('/vendor-prequal/requalify')) {
    return <VendorRequalify />;
  }

  // Public Procurement Client Request — external clients submit a
  // request without needing to log in.
  if (window.location.pathname.startsWith('/procurement/client-request')) {
    return <ProcurementClientRequestApply />;
  }

  // Show account pending / request access if error=account_pending from SSO
  if (showAccountPending) {
    return (
      <div className="pt-safe-mobile">
        <RequestAccess
          onLogout={async () => {
            setShowAccountPending(false);
            await removeAuthUser();
            window.location.href = '/';
          }}
        />
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner message="Authenticating..." />
      </div>
    );
  }

  // The desk as a panel in another site's page (SharePoint). Nothing else of
  // the app renders in the frame, the login page included: the panel signs
  // the person in silently, or says why it could not.
  if (isEmbeddedPanel()) {
    return (
      <React.Suspense fallback={<LoadingSpinner message="Loading..." />}>
        <ApprovalsDeskPanel user={user} />
      </React.Suspense>
    );
  }

  if (!user) {
    // Save deep link URL for restoration after SSO login (e.g., QR code scan, email link)
    const currentPath = window.location.pathname;
    const hasQueryView = new URLSearchParams(window.location.search).has('view');
    const isDeepLink = /^\/client-invoices\/[A-Za-z0-9]+/.test(currentPath) ||
                       /^\/estimates\/[A-Za-z0-9]+/.test(currentPath) ||
                       hasQueryView;
    if (isDeepLink) {
      // Save deep link with persistent storage (uses Capacitor Preferences on mobile)
      setPendingDeepLink(window.location.href).catch(() => {});
    }
    return <div className="pt-safe-mobile"><Login onLogin={handleLogin} /></div>;
  }
  // In the provider: Request Access sends this session's token (POST
  // /auth/request-access is the one route an inactive account may reach).
  if (!user.isActive) {
    return (
      <UserProvider value={user}>
        <div className="pt-safe-mobile"><RequestAccess onLogout={handleLogout} /></div>
      </UserProvider>
    );
  }

  // Determine current view context for deep-link routes
  const currentPath = parseUrl().path;
  const isPayslipsView = currentPath === '/hr/self-service/payslips' || activeItem === 'HRPayslips';
  const isDeepLinkView = !!(urlEstimateId || urlInvoiceId || isPayslipsView);

  // Compute active item for sidebar based on current view
  const computedActiveItem = urlEstimateId
    ? 'Estimate Detail'
    : urlInvoiceId
      ? 'ClientInvoiceDetail'
      : isPayslipsView
        ? 'HRPayslips'
        : activeItem;

  // Render deep-link content if applicable
  const renderDeepLinkContent = (): React.ReactNode => {
    if (urlEstimateId) {
      return (
        <main className="relative min-h-0 flex-1 bg-gray-100 p-6 overflow-y-auto pb-safe-mobile">
          <ProcurementEstimateDetail
            estimateId={urlEstimateId}
            user={user}
            onBack={() =>
              backOrList('/procurement/estimates', () => {
                setUrlEstimateId(null);
                navigate('ProcurementEstimatesList');
              })
            }
            onShowRequest={() =>
              backOrList('/procurement/requests', () => {
                setUrlEstimateId(null);
                navigate('ClientRequestList');
              })
            }
          />
        </main>
      );
    }

    if (urlInvoiceId) {
      return (
        <main className="relative min-h-0 flex-1 bg-gray-100 p-6 overflow-y-auto pb-safe-mobile">
          <ClientInvoiceDetail
            id={urlInvoiceId}
            onBack={() =>
              backOrList('/client-invoices', () => {
                setUrlInvoiceId(null);
                navigate('ClientInvoiceList');
              })
            }
          />
        </main>
      );
    }

    if (isPayslipsView) {
      return (
        <main className="relative min-h-0 flex-1 bg-gray-100 p-6 overflow-y-auto pb-safe-mobile">
          <PayslipsSelfService />
        </main>
      );
    }

    return null;
  };

  const deepLinkContent = renderDeepLinkContent();

  const showGrips = myProfile.profile.preferences.showGrips;

  const showEnvBanner = !isDeepLinkView && environment !== 'PRODUCTION' && !isBannerDismissed;

  // ----- Unified authenticated app layout -----
  return (
    <UserProvider value={user}>
    <ActingAsProvider>
    <SidebarProvider
      value={{ isSidebarOpen, setIsSidebarOpen, isSidebarCollapsed, setIsSidebarCollapsed }}
    >
    <ModalProvider>
    <div className="flex h-screen flex-col text-gray-800 overflow-hidden">
      {/* Offline/online connectivity banner (works on both web and native) */}
      {!isDeepLinkView && <OfflineBanner />}

      {/* Environment banner - only shown for normal app views. In flow above
          the top bar rather than fixed over it, so it never covers the bar. */}
      {showEnvBanner && (
        <div
          className={`flex-shrink-0 px-4 py-2 text-center font-bold flex items-center justify-center px-safe ${
            environment === 'LOCAL' ? 'bg-blue-600 text-white' : 'bg-yellow-500 text-black'
          }`}
        >
          <span>{environment === 'LOCAL' ? 'LOCAL DEVELOPMENT' : 'STAGING ENVIRONMENT'}</span>
          <CloseButton onClick={() => setIsBannerDismissed(true)} label="Dismiss banner" className="ml-4" />
        </div>
      )}

      {/* The top bar spans the full width; the sidebar and the page sit in the
          row beneath it, so the sidebar starts at the bar's bottom edge. */}
      <TopBar
        user={user}
        onLogout={handleLogout}
        onHome={() => navigate('Home')}
        onNavigate={endLandingHold}
        screenName={screenNameFor(computedActiveItem)}
      />

      <div className="relative flex min-h-0 flex-1">
        <Sidebar
          user={user}
          activeItem={computedActiveItem}
          setActiveItem={(view) => navigate(view)}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />


        {/* The page. It carries the sidebar offset once, so no page has to. */}
        <div className={`flex min-w-0 flex-1 flex-col ${mainOffsetClass(isSidebarCollapsed)}`}>
          {/* Render deep-link content or normal MainContent */}
          {deepLinkContent || (landingPending ? (
            <main className="min-h-0 flex-1 bg-gray-200">
              <LoadingSpinner fullScreen message="Loading..." />
            </main>
          ) : (
            <MainContent
              user={user}
              token={user.token}
              activeItem={activeItem}
              setActiveItem={setActiveItem}
              selectedRequestId={selectedRequestId}
              setSelectedRequestId={setSelectedRequestId}
              selectedInvoiceId={selectedInvoiceId}
              setSelectedInvoiceId={setSelectedInvoiceId}
              selectedJobId={selectedJobId}
              setSelectedJobId={setSelectedJobId}
              setActiveCategory={setActiveCategory}
              selectedRateHeaderId={selectedRateHeaderId}
            />
          ))}
        </div>
      </div>

      {/* Single ChatbotWidget instance for all authenticated views, unless the
          person has hidden GRiPS in Settings. */}
      {showGrips && <ChatbotWidget />}
    </div>
    </ModalProvider>
    </SidebarProvider>
    </ActingAsProvider>
    </UserProvider>
  );
}
